import {
  addDays,
  differenceInCalendarDays,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isWithinInterval,
  parse,
  startOfMonth,
  startOfYear,
} from "date-fns";
import { ru } from "date-fns/locale";

import { IvideonVehicleAnalytics } from "~/entity/Ivideon";
import { infoBlockDataIProps } from "~/entity/Ivideon/model/schema";
import { fromZerotoNaN } from "~/pages/IvideonDealerSummary2/helper";
import { period } from "~/pages/IvideonDealerSummary2/options";

import { dataBarIProps } from ".";

export const infoBlockData = (
  data: IvideonVehicleAnalytics,
): infoBlockDataIProps => {
  return {
    totalVehicles: data?.totalVehicles ?? 0,
    uniqueVehicles: data?.uniqueVehicles ?? 0,
    newVehicles: data?.newVehicles ?? 0,
    returningVehicles: data?.returningVehicles ?? 0,
  };
};

export const VEHICLE_DATA_KEYS = [
  "totalVehicles",
  "newVehicles",
  "returningVehicles",
  "uniqueVehicles",
] as const;

export type VehicleKey = (typeof VEHICLE_DATA_KEYS)[number];

export const getCarsDataHours = ({
  data,
  setDataBar,
  setLabels,
}: Omit<getCarsDataIProps, "startDate" | "endDate" | "variant">) => {
  const acc = VEHICLE_DATA_KEYS.reduce(
    (obj, key) => ({ ...obj, [key]: [] }),
    {} as Record<string, number[]>,
  );
  const dateBarsLabel: string[] = [];

  data.periodStats.forEach((item) => {
    if (!item) return;

    VEHICLE_DATA_KEYS.forEach((key) => {
      // item[key] теперь понимается как число
      acc[key].push(item[key as keyof typeof item] as number);
    });

    dateBarsLabel.push(`${String(item.period).padStart(2, "0")}:00`);
  });

  setLabels(dateBarsLabel);

  const formattedData: Record<string, (number | typeof NaN)[]> = {};
  VEHICLE_DATA_KEYS.forEach((key) => {
    formattedData[key] = fromZerotoNaN(acc[key]);
  });

  setDataBar(formattedData as any);
};

export const getCarsDataDays = ({
  difference,
  startDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getCarsDataIProps, "endDate" | "variant"> & { difference: number }) => {
  const dateBarsLabel: string[] = [];
  const acc = VEHICLE_DATA_KEYS.reduce(
    (obj, key) => ({ ...obj, [key]: [] }),
    {} as Record<VehicleKey, number[]>,
  );

  for (let i = 0; i < difference; i++) {
    const currentDate = addDays(new Date(startDate), i);
    const label = format(currentDate, "dd.MM");
    const labelCompare = format(currentDate, "yyyy-MM-dd");

    dateBarsLabel.push(label);

    const statsForDay = data.periodStats.find(
      (stats) => stats?.periodStart === labelCompare,
    );

    VEHICLE_DATA_KEYS.forEach((key) => {
      const value = statsForDay ? (statsForDay[key] as number) : 0;
      acc[key].push(value);
    });
  }

  setLabels(dateBarsLabel);

  const formattedData = VEHICLE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as dataBarIProps);

  setDataBar(formattedData);
};

export const getCarsDataWeeks = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getCarsDataIProps, "variant">) => {
  const parseFormat = "yyyy-MM-dd";
  const dateBarsLabel: string[][] = [];

  const acc = VEHICLE_DATA_KEYS.reduce(
    (obj, key) => ({ ...obj, [key]: [] }),
    {} as Record<VehicleKey, number[]>,
  );

  const weeks = eachWeekOfInterval(
    { start: startDate, end: endDate },
    { weekStartsOn: 1 },
  );

  weeks.forEach((weekStart, index) => {
    let currentRangeStart = weekStart;
    let currentRangeEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

    if (index === 0) currentRangeStart = startDate;
    if (index === weeks.length - 1) currentRangeEnd = endDate;

    dateBarsLabel.push([
      format(currentRangeStart, "dd.MM", { locale: ru }),
      format(currentRangeEnd, "dd.MM"),
    ]);

    const currentWeekSums = VEHICLE_DATA_KEYS.reduce(
      (obj, key) => ({ ...obj, [key]: 0 }),
      {} as Record<VehicleKey, number>,
    );

    data.periodStats.forEach((item) => {
      if (!item?.periodStart) return;

      const itemDate = parse(item.periodStart, parseFormat, new Date());
      const isRange = isWithinInterval(itemDate, {
        start: currentRangeStart,
        end: currentRangeEnd,
      });

      if (isRange) {
        VEHICLE_DATA_KEYS.forEach((key) => {
          currentWeekSums[key] += (item[key] as number) || 0;
        });
      }
    });

    VEHICLE_DATA_KEYS.forEach((key) => {
      acc[key].push(currentWeekSums[key]);
    });
  });

  setLabels(dateBarsLabel);

  const formattedData = VEHICLE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as dataBarIProps);

  setDataBar(formattedData);
};

export const getCarsDataMonths = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getCarsDataIProps, "variant">) => {
  const parseFormat = "yyyy-MM-dd";
  const dateBarsLabel: string[][] = [];
  const acc = VEHICLE_DATA_KEYS.reduce(
    (obj, key) => ({ ...obj, [key]: [] }),
    {} as Record<VehicleKey, number[]>,
  );

  const months = eachMonthOfInterval({ start: startDate, end: endDate });

  months.forEach((monthStart, index) => {
    let currentRangeStart = startOfMonth(monthStart);
    let currentRangeEnd = endOfMonth(monthStart);

    if (index === 0) currentRangeStart = startDate;
    if (index === months.length - 1) currentRangeEnd = endDate;

    dateBarsLabel.push([
      format(monthStart, "LLLL", { locale: ru }),
      format(monthStart, "yyyy"),
    ]);

    const currentMonthSums = VEHICLE_DATA_KEYS.reduce(
      (obj, key) => ({ ...obj, [key]: 0 }),
      {} as Record<VehicleKey, number>,
    );

    data.periodStats.forEach((item) => {
      if (!item?.periodStart) return;

      const itemDate = parse(item.periodStart, parseFormat, new Date());
      const isInside = isWithinInterval(itemDate, {
        start: currentRangeStart,
        end: currentRangeEnd,
      });

      if (isInside) {
        VEHICLE_DATA_KEYS.forEach((key) => {
          currentMonthSums[key] += (item[key] as number) || 0;
        });
      }
    });

    VEHICLE_DATA_KEYS.forEach((key) => acc[key].push(currentMonthSums[key]));
  });

  setLabels(dateBarsLabel as any);

  const finalData = VEHICLE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as dataBarIProps);

  setDataBar(finalData);
};

export const getCarsDataYears = ({
  startDate,
  endDate,
  data,
  setDataBar,
  setLabels,
}: Omit<getCarsDataIProps, "variant">) => {
  const parseFormat = "yyyy-MM-dd";
  const dateBarsLabel: string[] = [];
  const acc = VEHICLE_DATA_KEYS.reduce(
    (obj, key) => ({ ...obj, [key]: [] }),
    {} as Record<VehicleKey, number[]>,
  );

  const years = eachYearOfInterval({ start: startDate, end: endDate });

  years.forEach((yearStart, index) => {
    let currentRangeStart = startOfYear(yearStart);
    let currentRangeEnd = endOfYear(yearStart);

    if (index === 0) currentRangeStart = startDate;
    if (index === years.length - 1) currentRangeEnd = endDate;

    dateBarsLabel.push(format(yearStart, "yyyy"));

    const currentYearSums = VEHICLE_DATA_KEYS.reduce(
      (obj, key) => ({ ...obj, [key]: 0 }),
      {} as Record<VehicleKey, number>,
    );

    data.periodStats.forEach((item) => {
      if (!item?.periodStart) return;

      const itemDate = parse(item.periodStart, parseFormat, new Date());
      if (
        isWithinInterval(itemDate, {
          start: currentRangeStart,
          end: currentRangeEnd,
        })
      ) {
        VEHICLE_DATA_KEYS.forEach((key) => {
          currentYearSums[key] += item[key] || 0;
        });
      }
    });

    VEHICLE_DATA_KEYS.forEach((key) => acc[key].push(currentYearSums[key]));
  });

  setLabels(dateBarsLabel);

  const finalData = VEHICLE_DATA_KEYS.reduce((obj, key) => {
    obj[key] = fromZerotoNaN(acc[key]);
    return obj;
  }, {} as dataBarIProps);

  setDataBar(finalData);
};

export interface getCarsDataIProps {
  startDate: Date;
  endDate: Date;
  data: IvideonVehicleAnalytics;
  variant: string | null;
  setDataBar: (props: dataBarIProps) => void;
  setLabels: (props: string[] | string[][]) => void;
}

export const getCarsData = ({
  startDate,
  endDate,
  data,
  variant,
  setDataBar,
  setLabels,
}: getCarsDataIProps) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

  const difference = differenceInCalendarDays(end, start) + 1;
  const params = {
    difference,
    startDate: start,
    endDate: end,
    data,
    variant,
    setDataBar,
    setLabels,
  };
  if (difference >= period.year) {
    getCarsDataYears(params);
  } else if (difference >= period.month && difference < period.year) {
    getCarsDataMonths(params);
  } else if (difference <= period.week && difference >= period.day) {
    getCarsDataWeeks(params);
  } else if (difference < period.day && difference !== period.hour) {
    getCarsDataDays(params);
  } else if (difference === period.hour) {
    getCarsDataHours({ data, setDataBar, setLabels });
  }
};
